import React from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled from "styled-components";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import Select from "common/Select/Select";
import useTrailingAndTakeProfit, {
  UseTrailingAndTakeProfitProps,
} from "@shared/features/bots/BotForm/useTrailingAndTakeProfit";
import { LEVERAGED_TRAILING_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import { LeverageTrailingText } from "features/bots/BotForm/common/styles";
import OverrideToggle from "features/bots/BotForm/common/OverrideToggle";
import Checkbox from "common/Checkbox/Checkbox";
import Text from "common/Typography/Text";
import { OverrideType } from "@shared/features/bots/BotForm/common/types.ts";
import { OrderTypes } from "@shared/features/trades/common/types.ts";

const TrailingEntryAndTakeProfit = ({
  orderType,
}: UseTrailingAndTakeProfitProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    value,
    onChange,
    lastTargetOnly,
    onLastTargetOnlyChange,
    showLastTargetOnly,
    fieldType,
    overrideFieldType,
    options,
    placeholder,
    title,
    isLeveraged,
  } = useTrailingAndTakeProfit({
    orderType,
  });

  const content = (
    <>
      <div style={{ maxWidth: "50%" }}>
        <Select
          style={{ minWidth: 250 }}
          value={value}
          onChange={onChange}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          placeholder={placeholder}
          options={options?.map((option) => ({
            value: option.value,
            label: option.name,
          }))}
          mobileTitle={title}
        />
      </div>
      {isLeveraged && (
        <LeverageTrailingText>
          * {t(LEVERAGED_TRAILING_MESSAGE)}
        </LeverageTrailingText>
      )}
      {showLastTargetOnly && (
        <LastTargetOnlyWrapper
          onClick={() => onLastTargetOnlyChange(!lastTargetOnly)}
        >
          <Checkbox
            checked={lastTargetOnly}
            style={{ marginRight: 12 }}
            $size={18}
          />
          <Text as="div">
            {t("labels.trailing_take_profit_last_target_only")}
          </Text>
        </LastTargetOnlyWrapper>
      )}
      <OverrideToggle overrideField={overrideFieldType} />
    </>
  );

  return (
    <Wrapper>
      <TitleWithToggle
        title={title}
        content={content}
        fieldType={fieldType}
        onOverrideChange={(overrideType) => {
          if (
            orderType === OrderTypes.TAKE_PROFIT &&
            overrideType !== OverrideType.PERSONAL &&
            lastTargetOnly
          ) {
            onLastTargetOnlyChange(false);
          }
        }}
      />
    </Wrapper>
  );
};

export default TrailingEntryAndTakeProfit;

const Wrapper = styled.div`
  margin-top: 16px;
`;

const LastTargetOnlyWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding-top: 12px;
`;
